import {assert} from 'Asserts';
import deepCopy from 'lib/deepCopy';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {JSONValue} from 'util/Types';

export function getConfigShapeAtConfigShapePath(
  configShapePath: Array<string>,
  configShape: PipelineConfigJsonSpec,
  config: Record<string, JSONValue> | null
): PipelineConfigJsonSpec {
  return configShapePath.reduce((narrowedConfigShape, configKey, currentIndex) => {
    assert(
      narrowedConfigShape.kind === 'shape' && narrowedConfigShape.fields.hasOwnProperty(configKey),
      'Invalid path for config shape'
    );
    if (
      narrowedConfigShape.fields[configKey].kind === 'taggedUnion' &&
      currentIndex !== configShapePath.length - 1
    ) {
      // Choose which configShape based on the current value of the tagged union in the config object
      const tagKey = narrowedConfigShape.fields[configKey].tagKey;
      const tagValue = maybeGetValueFromConfig(config, [
        ...configShapePath.slice(0, currentIndex + 1),
        tagKey,
      ]);
      let choice: PipelineConfigJsonSpec | undefined;
      if (tagValue == null) {
        // default to the first choice
        choice = narrowedConfigShape.fields[configKey].choices[0];
      } else {
        choice = narrowedConfigShape.fields[configKey].choices.find(
          (choice) => choice.fields[tagKey].kind === 'string' && choice.fields[tagKey].value === tagValue
        );
      }

      assert(choice != null, 'No choice found for tagged union', {
        configShapePath: configShapePath.join('.'),
        configKey,
        tagValue,
      });
      return choice;
    }
    // TODO(BNCH-120657): Update path to work for arrays
    return narrowedConfigShape.fields[configKey];
  }, configShape);
}

export function maybeGetValueFromConfig(
  config: Record<string, JSONValue> | null,
  configShapePath: Array<string>
): JSONValue | null {
  let current: JSONValue = deepCopy(config);
  for (const key of configShapePath) {
    // TODO(BNCH-120657): Update to work for paths nested within arrays
    if (current == null || typeof current !== 'object' || Array.isArray(current)) {
      // For draft steps config is empty so we return null
      return null;
    }
    current = current[key];
  }
  return current;
}

export function setValueInConfig(
  config: Record<string, JSONValue>,
  configShapePath: Array<string>,
  newValue: JSONValue
): Record<string, JSONValue> {
  if (configShapePath.length === 0) {
    assert(
      newValue != null && typeof newValue === 'object' && !Array.isArray(newValue),
      'New value must be an object if path is empty'
    );
    return newValue;
  }
  const configCopy = deepCopy(config);

  // TODO(BNCH-120657): Update to work for arrays
  let current = configCopy;
  for (let i = 0; i < configShapePath.length - 1; i++) {
    const key = configShapePath[i];
    assert(
      current[key] != null && typeof current[key] === 'object' && !Array.isArray(current[key]),
      `Cannot set value at configShapePath: parent is not an object`,
      {configShapePath: configShapePath.join('.')}
    );
    current = current[key];
  }

  const finalKey = configShapePath[configShapePath.length - 1];
  assert(
    current != null && typeof current === 'object',
    'Cannot set value at configShapePath because parent is not an object',
    {
      configShapePath: configShapePath.join('.'),
    }
  );
  current[finalKey] = newValue;
  return configCopy;
}
